import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { ensureStages, useTaskStages } from '@/features/projects/api'
import { patchFromTaskRead, toTask } from '@/shared/api/adapt'
import { api } from '@/shared/api/client'
import type { Page, TaskListItem, TaskRead } from '@/shared/api/contracts'
import { errorMessage } from '@/shared/components/data'
import { useT } from '@/shared/i18n'
import { qk } from '@/shared/api/query-keys'
import { ensureUserIndex } from '@/shared/hooks/use-users'
import type { Stage, Task, TaskCreateInput, TaskPatchInput } from '@/shared/types/domain'

import { sortTasks } from './reorder'

/**
 * CRUD de tareas con actualizacion optimista.
 *
 * La misma tarea vive en varias caches a la vez (la lista del proyecto,
 * `/due-soon`, `/me/tasks`). Si el optimismo tocara solo la lista donde ocurrio
 * el click, marcar algo como hecho desde `/due-soon` lo dejaria sin tachar en
 * el proyecto hasta el refetch. Por eso se parchea por PREFIJO de query key, no
 * por key exacta.
 */

const TASK_TREES = [qk.tasks.all, qk.dueSoon.all] as const

type Snapshot = [readonly unknown[], unknown][]

function snapshot(client: QueryClient): Snapshot {
  return TASK_TREES.flatMap((key) => client.getQueriesData({ queryKey: key }))
}

function restore(client: QueryClient, entries: Snapshot): void {
  for (const [key, data] of entries) client.setQueryData(key, data)
}

/** Aplica `update` a cada `Task[]` cacheado, venga de la lista que venga. */
function patchLists(client: QueryClient, update: (tasks: Task[]) => Task[]): void {
  for (const key of TASK_TREES) {
    client.setQueriesData({ queryKey: key }, (old: unknown) =>
      Array.isArray(old) ? update(old as Task[]) : old,
    )
  }
}

async function cancelTaskQueries(client: QueryClient): Promise<void> {
  await Promise.all(TASK_TREES.map((key) => client.cancelQueries({ queryKey: key })))
}

function invalidateTaskQueries(client: QueryClient): void {
  for (const key of TASK_TREES) void client.invalidateQueries({ queryKey: key })
  // Los contadores del portafolio se derivan de las tareas: si no se invalidan,
  // la fila sigue diciendo "3 vencidas" despues de completar la tercera.
  void client.invalidateQueries({ queryKey: qk.portfolio.all })
  void client.invalidateQueries({ queryKey: qk.projects.all })
  void client.invalidateQueries({ queryKey: qk.companies.all })
}

/* --------------------------------------------------------------- queries - */

/**
 * Las notas de una tarea (`description_md`), que **la lista no trae**.
 *
 * Las listas devuelven `TaskListItem`, sin notas, y el panel de detalle se
 * armaba con la fila de la lista: las notas se guardaban pero al volver a
 * abrir el panel se veían vacías, y quien escribía ahí pisaba lo que había
 * sin saberlo. El panel las lee de `GET /tasks/{id}`, que sí las trae.
 */
export function useTaskNotes(taskId: string | null) {
  return useQuery({
    queryKey: qk.tasks.detail(taskId ?? ''),
    enabled: taskId !== null,
    queryFn: async () => (await api.get<TaskRead>(`tasks/${taskId}`)).description_md ?? '',
  })
}

/**
 * Las tareas del proyecto. La MISMA query alimenta la lista y el tablero: es lo
 * que hace imposible que las dos vistas se contradigan.
 *
 * `GET /projects/{id}/tasks` **no existe**: el filtro por proyecto es un query
 * param de `GET /tasks`, que además devuelve `Page[TaskListItem]`. Y ese DTO
 * **no trae `stage_id`**, solo `stage_label`: el id se resuelve contra el set de
 * fases de tareas (dentro de un set las etiquetas son únicas), que es lo que
 * necesita el tablero para agrupar y para mover.
 *
 * `refetchOnWindowFocus: false` porque es una pantalla de orden (plan 3.4): un
 * refetch al volver el foco con un drop en vuelo pisa la cache optimista.
 *
 * Se ordena por rank UNA vez, al llegar: de ahi en mas la cache es el orden. Si
 * la lista se reordenara por rank en cada render, el movimiento optimista de un
 * drag (que mueve el indice y no el rank) se desharia solo.
 */
export function useProjectTasks(projectId: string) {
  const client = useQueryClient()

  return useQuery({
    queryKey: qk.tasks.list(projectId),
    // El mapeo va en el `queryFn`, NO en `select`: con `select` la cache
    // guardaría el DTO crudo y todos los updates optimistas —que escriben
    // `Task[]` con `setQueriesData`— quedarían escribiendo en la forma
    // equivocada. La cache guarda lo que la pantalla usa.
    queryFn: async () => {
      const [users, stages, page] = await Promise.all([
        ensureUserIndex(client),
        ensureStages(client, 'task'),
        api.get<Page<TaskListItem>>(`tasks?project_id=${encodeURIComponent(projectId)}&limit=200`),
      ])
      return sortTasks(page.items.map((item) => toTask(item, { users, taskStages: stages })))
    },
    enabled: Boolean(projectId),
    refetchOnWindowFocus: false,
  })
}

/* ------------------------------------------------------------- mutations - */

export function useCreateTask(projectId: string) {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    mutationKey: ['tasks', 'create', projectId],
    mutationFn: (input: TaskCreateInput) => api.post<TaskRead>('tasks', { ...input }),
    // `TaskRead` es más chico que la fila del listado (no trae nombre de
    // proyecto ni de empresa), así que empujarlo a la cache dejaría una fila a
    // medias. Se invalida y la lista vuelve completa.
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.tasks.list(projectId) })
    },
    onError: (error) => toast.error(errorMessage(error, t('tasks.error.create'))),
    onSettled: () => invalidateTaskQueries(client),
  })
}

export interface UpdateTaskVariables {
  id: string
  patch: TaskPatchInput
  /** Como se ve el cambio ANTES de que conteste el server. */
  optimistic?: (task: Task) => Task
}

export function useUpdateTask() {
  const client = useQueryClient()
  const stages = useTaskStages()
  const t = useT()

  return useMutation({
    mutationKey: ['tasks', 'update'],
    mutationFn: ({ id, patch }: UpdateTaskVariables) =>
      api.patch<TaskRead>(`tasks/${id}`, { ...patch }),
    onMutate: async ({ id, optimistic }) => {
      await cancelTaskQueries(client)
      const previous = snapshot(client)
      if (optimistic) {
        patchLists(client, (tasks) =>
          tasks.map((task) => (task.id === id ? optimistic(task) : task)),
        )
      }
      return { previous }
    },
    onError: (error, _variables, context) => {
      if (context?.previous) restore(client, context.previous)
      toast.error(errorMessage(error, t('tasks.error.save')))
    },
    onSuccess: (task) => {
      const patch = patchFromTaskRead(task, stages.data ?? [])
      patchLists(client, (tasks) =>
        tasks.map((item) => (item.id === task.id ? { ...item, ...patch } : item)),
      )
    },
    onSettled: () => invalidateTaskQueries(client),
  })
}

export function useDeleteTask() {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    mutationKey: ['tasks', 'delete'],
    mutationFn: (id: string) => api.delete<void>(`tasks/${id}`),
    onMutate: async (id) => {
      await cancelTaskQueries(client)
      const previous = snapshot(client)
      patchLists(client, (tasks) => tasks.filter((task) => task.id !== id))
      return { previous }
    },
    onError: (error, _id, context) => {
      if (context?.previous) restore(client, context.previous)
      toast.error(errorMessage(error, t('tasks.error.delete')))
    },
    onSuccess: () => toast.success(t('tasks.toast.deleted')),
    onSettled: () => invalidateTaskQueries(client),
  })
}

/**
 * Completar / reabrir, que es el 80% de las mutaciones reales.
 *
 * **No es un PATCH.** `TaskUpdate` no tiene `completed`: terminar una tarea es
 * `POST /tasks/{id}/complete` y reabrirla `POST /tasks/{id}/reopen`. Son
 * endpoints propios porque completar además mueve la tarea a la fase `done` del
 * set y sella `completed_at`; un campo booleano en un PATCH no podría hacer eso
 * sin que el PATCH signifique dos cosas.
 */
export function useCompleteTask() {
  const client = useQueryClient()
  const stages = useTaskStages()
  const t = useT()

  return useMutation({
    mutationKey: ['tasks', 'complete'],
    mutationFn: ({ id, done }: { id: string; done: boolean }) =>
      api.post<TaskRead>(`tasks/${id}/${done ? 'complete' : 'reopen'}`),
    onMutate: async ({ id, done }) => {
      await cancelTaskQueries(client)
      const previous = snapshot(client)
      const doneStage = (stages.data ?? []).find((stage: Stage) => stage.category === 'done')

      patchLists(client, (tasks) =>
        tasks.map((task) =>
          task.id === id
            ? {
                ...task,
                completed_at: done ? new Date().toISOString() : null,
                stage: done ? (doneStage ?? task.stage) : task.stage,
              }
            : task,
        ),
      )
      return { previous }
    },
    onError: (error, _variables, context) => {
      if (context?.previous) restore(client, context.previous)
      toast.error(errorMessage(error, t('tasks.error.save')))
    },
    onSuccess: (task) => {
      const patch = patchFromTaskRead(task, stages.data ?? [])
      patchLists(client, (tasks) =>
        tasks.map((item) => (item.id === task.id ? { ...item, ...patch } : item)),
      )
    },
    onSettled: () => invalidateTaskQueries(client),
  })
}

/** Firma vieja, mantenida para los componentes que ya la llamaban. */
export function useToggleTaskDone() {
  const complete = useCompleteTask()
  return (task: Task, done: boolean) => complete.mutate({ id: task.id, done })
}
